/**
 * プロダクトレベルのダウンロード状態管理フック
 *
 * 複数コンポーネントで共通する「全エピソードのオフライン判定」
 * 「アクティブダウンロード判定」「一括削除」のロジックを集約。
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { deleteOfflineEpisodesByProduct, isProductFullyOffline } from '@/lib/db'
import { useDownloadStore } from '@/store/useDownloadStore'

/**
 * 指定エピソードIDリストにアクティブなダウンロードがあるかを返す
 */
export const useHasActiveDownload = (episodeIds: number[]) => {
  const downloads = useDownloadStore((s) => s.downloads)
  return useMemo(() => {
    return episodeIds.some((id) => {
      const dl = downloads.get(id)
      return dl && (dl.status === 'downloading' || dl.status === 'queued' || dl.status === 'saving')
    })
  }, [episodeIds, downloads])
}

/**
 * 指定エピソードIDリストが全てオフライン保存済みかを返す
 *
 * downloads の変更をトリガーにして再チェックする。
 */
export const useIsProductOffline = (episodeIds: number[]) => {
  const downloads = useDownloadStore((s) => s.downloads)
  const [isFullyOffline, setIsFullyOffline] = useState(false)

  useEffect(() => {
    if (episodeIds.length === 0) return
    let cancelled = false
    isProductFullyOffline(episodeIds)
      .then((result) => {
        if (!cancelled) setIsFullyOffline(result)
      })
      .catch(() => {
        if (!cancelled) setIsFullyOffline(false)
      })
    return () => {
      cancelled = true
    }
  }, [episodeIds, downloads])

  return { isFullyOffline, setIsFullyOffline }
}

/**
 * プロダクト全エピソードのオフラインデータを削除する
 */
export const useRemoveProductOffline = (productId: number | undefined, setIsFullyOffline: (v: boolean) => void) => {
  return useCallback(() => {
    if (!productId) return
    deleteOfflineEpisodesByProduct(productId).then(() => setIsFullyOffline(false))
  }, [productId, setIsFullyOffline])
}
